import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { SectionToggle } from './SectionToggle';
import { showAlignerWear } from '@odo/shared-ui/clinical/alignerFields';
import { normalizeDays } from '@odo/shared-ui/clinical/formatDays';
import { formatOrthopulseDisplay } from './orthopulse';
import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import type { AlignerFieldToggles } from '@odo/shared-ui/clinical/types';

const fieldLabelClasses = 'block text-xs font-medium text-gray-500 mb-1';

interface FieldProps {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    type?: 'text' | 'date';
    placeholder?: string;
    hideLabel?: boolean;
}

function Field({
    label,
    value,
    onChange,
    readOnly = false,
    type = 'text',
    placeholder,
    hideLabel = false
}: FieldProps) {
    const readOnlyClasses = readOnly ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '';

    return (
        <div className="flex flex-col">
            {!hideLabel && <label className={fieldLabelClasses}>{label}</label>}
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                readOnly={readOnly}
                placeholder={placeholder}
                className={`${baseFieldClasses} ${readOnlyClasses} h-9`}
            />
        </div>
    );
}

interface DaysFieldProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

function DaysField({ value, onChange, placeholder }: DaysFieldProps) {
    const displayValue = normalizeDays(value);

    return (
        <div className="relative w-full">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600 pointer-events-none">
                every
            </span>
            <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={displayValue}
                onChange={(e) => onChange(normalizeDays(e.target.value))}
                placeholder={placeholder}
                className={`${baseFieldClasses} h-9 pl-12 pr-10 text-center`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600 pointer-events-none">
                days
            </span>
        </div>
    );
}

interface SelectFieldProps {
    value: string;
    onChange: (value: string) => void;
    options: readonly string[];
    placeholder?: string;
}

function SelectField({ value, onChange, options, placeholder = 'Select…' }: SelectFieldProps) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`${baseFieldClasses} appearance-none h-9 pr-9 ${value === '' ? 'text-gray-400' : ''}`}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((opt) => (
                    <option key={opt} value={opt} className="text-gray-800">
                        {opt}
                    </option>
                ))}
            </select>
            <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
    );
}

interface ToggleableFieldProps {
    label: string;
    enabled: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

function ToggleableField({ label, enabled, onToggle, children }: ToggleableFieldProps) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-medium text-gray-500 truncate">{label}</label>
                <SectionToggle enabled={enabled} onToggle={onToggle} size="sm" />
            </div>
            <div className={enabled ? undefined : 'opacity-40 pointer-events-none select-none'}>
                {children}
            </div>
        </div>
    );
}

export interface AlignerScheduleSectionProps {
    numberOfAligners: string;
    onNumberOfAlignersChange: (v: string) => void;
    nextAppointment: string;
    onNextAppointmentChange: (v: string) => void;
    nextAppointmentLabel: string;
    nextAppointmentPlaceholder?: string;
    dmScanFrequency: string;
    onDmScanFrequencyChange: (v: string) => void;
    alignerChangeFrequency: string;
    onAlignerChangeFrequencyChange: (v: string) => void;
    alignerWear: string;
    onAlignerWearChange: (v: string) => void;
    alignerWearOptions: readonly string[];
    orthopulseStatus?: string | null;
    toggles: AlignerFieldToggles;
    onTogglesChange: (next: AlignerFieldToggles) => void;
}

function AlignerScheduleSection({
    numberOfAligners,
    onNumberOfAlignersChange,
    nextAppointment,
    onNextAppointmentChange,
    nextAppointmentLabel,
    nextAppointmentPlaceholder,
    dmScanFrequency,
    onDmScanFrequencyChange,
    alignerChangeFrequency,
    onAlignerChangeFrequencyChange,
    alignerWear,
    onAlignerWearChange,
    alignerWearOptions,
    orthopulseStatus,
    toggles,
    onTogglesChange
}: AlignerScheduleSectionProps) {
    const orthopulseDisplay = formatOrthopulseDisplay(orthopulseStatus);

    const toggle = (key: keyof AlignerFieldToggles) => {
        onTogglesChange({ ...toggles, [key]: !toggles[key] });
    };

    const alignerWearVisible = showAlignerWear(toggles);

    return (
        <div className="grid grid-cols-2 gap-3">
            <ToggleableField
                label="Number of aligners to provide"
                enabled={toggles.numberOfAligners}
                onToggle={() => toggle('numberOfAligners')}
            >
                <Field
                    value={numberOfAligners}
                    onChange={onNumberOfAlignersChange}
                    placeholder="e.g., 12"
                    hideLabel
                />
            </ToggleableField>

            <ToggleableField
                label={nextAppointmentLabel}
                enabled={toggles.nextAppointment}
                onToggle={() => toggle('nextAppointment')}
            >
                <Field
                    value={nextAppointment}
                    onChange={onNextAppointmentChange}
                    placeholder={nextAppointmentPlaceholder}
                    hideLabel
                />
            </ToggleableField>

            <ToggleableField
                label="DM Scan Frequency"
                enabled={toggles.dmScanFrequency}
                onToggle={() => toggle('dmScanFrequency')}
            >
                <DaysField
                    value={dmScanFrequency}
                    onChange={onDmScanFrequencyChange}
                    placeholder="e.g., 14"
                />
            </ToggleableField>

            <ToggleableField
                label="Patient Aligner Change Frequency"
                enabled={toggles.alignerChangeFrequency}
                onToggle={() => toggle('alignerChangeFrequency')}
            >
                <DaysField
                    value={alignerChangeFrequency}
                    onChange={onAlignerChangeFrequencyChange}
                    placeholder="e.g., 7"
                />
            </ToggleableField>

            {alignerWearVisible && (
                <ToggleableField
                    label="Aligner Wear Schedule"
                    enabled={toggles.alignerWear}
                    onToggle={() => toggle('alignerWear')}
                >
                    <SelectField
                        value={alignerWear}
                        onChange={onAlignerWearChange}
                        options={alignerWearOptions}
                    />
                </ToggleableField>
            )}

            {/* Read-only info slot — muted to signal "display only, not editable". */}
            <div className="opacity-70">
                <Field label="Orthopulse" value={orthopulseDisplay} onChange={() => {}} readOnly />
            </div>
        </div>
    );
}

export { AlignerScheduleSection };
